<template>
    <b-form>
        <b-row class="mb-2">
            <!-- Empresa -->
            <b-col cols="12" md="6" xl="3" class="form-group" v-if="viewFilterCompany">
                <label for="id_empresa">Empresa</label>
                <el-select id="empresa" name="empresa" filterable clearable v-model="filter.company_id"
                    placeholder="Seleccione una opción" size="medium" remote :remote-method="searchCompany"
                    :disabled="loading" :loading="loading">
                    <el-option v-for="empresa in empresas" :key="empresa.id" :label="empresa.nombre"
                        :value="empresa.id" />
                </el-select>
            </b-col>

            <!-- País -->
            <b-col cols="12" md="6" xl="3" class="form-group"
                v-if="filter.company_id && allCountries.length > 1 && !screens.usability">
                <label for="country_id">País</label>
                <el-select id="country_id" name="country_id" v-model="filter.country_id" filterable clearable size="medium"
                    :disabled="!filter.company_id || loading" no-data-text="No hay datos que coincidan"
                    no-match-text="No hay datos que coincidan" class="select-with-flag">
                    <template slot="prefix" v-if="selectedCountryCode">
                        <div class="flag-prefix-container">
                            <img :src="`https://flagsapi.com/${selectedCountryCode}/flat/64.png`" class="flag-img-select" @error="$event.target.style.display='none'" @load="$event.target.style.display=''">
                        </div>
                    </template>
                    <el-option v-for="item in allCountries" :key="item.country.id" :label="item.country.name"
                        :value="item.country.id">
                        <div class="d-flex align-items-center">
                            <img :src="`https://flagsapi.com/${item.country.code.toUpperCase()}/flat/64.png`"
                                class="mr-2 flag-img-option" @error="$event.target.style.display='none'" @load="$event.target.style.display=''">
                            <span>{{ item.country.name }}</span>

                        </div>
                    </el-option>
                </el-select>
            </b-col>

            <!-- Ubicación (múltiple, filtrado por el país seleccionado) -->
            <b-col cols="12" md="6" xl="3" class="form-group" v-if="!screens.usability">
                <label for="id_ubicacion">Ubicación</label>
                <tree-select v-model="filter.slq_location_id" id="ubicacion" name="ubicacion" :multiple="true"
                    :defaultExpandLevel="Infinity" :options="treeWithChildrens" :normalizer="normalizer"
                    :show-count="true" :disabled="!filter.company_id"
                    placeholder="Selecciona una o más ubicaciones">
                    <template slot="option-label" slot-scope="{ node }">
                        <div class="d-flex align-items-center"
                            :class="{ 'ubicacion-otro-pais': isLockedByOtherCountry(node.id) }">
                            <span>{{ node.label }}</span>
                            <!-- País asignado directamente en el nodo -->
                            <span v-if="node.raw.company_country && node.raw.company_country.country" class="ml-2">
                                <img :src="`https://flagsapi.com/${node.raw.company_country.country.code.toUpperCase()}/flat/64.png`"
                                    class="flag-img-tree" @error="$event.target.style.display='none'" @load="$event.target.style.display=''">
                            </span>
                        </div>
                    </template>
                </tree-select>
            </b-col>

            <!-- Uso -->
            <b-col cols="12" md="6" xl="3" class="form-group" v-if="!screens.usability">
                <label for="in_use">Disponibilidad en inventario</label>
                <el-select id="in_use" name="in_use" filterable clearable v-model="filter.in_use"
                    placeholder="Seleccione una opción" size="medium" :disabled="loading">
                    <el-option label="Si" :value="1" />
                    <el-option label="No" :value="0" />
                </el-select>
            </b-col>


            <!-- Usuario -->
            <b-col cols="12" md="3" class="form-group" v-if="screens.usability">
                <label for="user_id">Usuario</label>
                <el-select id="user_id" name="user_id" filterable clearable v-model="filter.user_id"
                    placeholder="Seleccione una opción" size="medium" :disabled="loading">
                    <el-option v-for="user in users" :key="user.id" :label="toTitleCase(user.name)" :value="user.id" />
                </el-select>
            </b-col>

            <!-- Fecha de reporte -->
            <!-- Label noverseen  md  solo en mobile -->
            <label class="ml-2 d-md-none">Fecha de reporte</label>
            <b-col cols="12" md="4" v-if="screens.usability" class="5">
                <label class="d-none d-md-block">Fecha de reporte</label>
                <el-date-picker v-model="filter.date_report" type="daterange" range-separator="a"
                    start-placeholder="Desde" end-placeholder="Hasta" :picker-options="pickerOptions" size="medium"
                    :disabled="loading">
                </el-date-picker>
            </b-col>

        </b-row>
    </b-form>
</template>

<script>
import TreeSelect from "@riophae/vue-treeselect";
import { mapActions, mapMutations, mapState } from "vuex";
import { validarPermisoComponente } from "@/plugins/acl";
import moment from "moment";
import debounce from 'lodash/debounce';
import useCompanyCountries from '../../../composables/country/useCompanyCountries'
import useCompanyLocationsTree from '../../../composables/location/useCompanyLocationsTree'

export default {
    name: "FormSearch",
    components: { TreeSelect },
    mixins: [useCompanyCountries, useCompanyLocationsTree],
    data() {
        return {
            filter: {
                company_id: null,
                slq_location_id: [],
                date_report: null,
                in_use: 1,
                country_id: null,
            },
            from: moment().subtract(5, "year").format("YYYY-MM-DD"),
            to: moment().format("YYYY-MM-DD"),
            pickerOptions: {
                disabledDate(time) {
                    const today = new Date();
                    const fiveYearsAgo = new Date();
                    fiveYearsAgo.setFullYear(today.getFullYear() - 5);
                    return (
                        time.getTime() > today.getTime() ||
                        time.getTime() < fiveYearsAgo.getTime()
                    );
                },
            },
            validarPermisoComponente: validarPermisoComponente,
            viewFilterCompany: false,
        };
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
    },
    async created() {
        this.debouncedLocationSearch = debounce(this.searchInfoFromLocations, 400);
        this.load(false);
        if (this.validarPermisoComponente(["ver-dashboard-todas-las-empresas"])) {
            this.viewFilterCompany = true;
        } else {
            this.viewFilterCompany = false;
            this.filter.company_id = await this.obtenerIdEmpresaUsuario();
        }
        // Inicializa los filtros en el estado Vuex al cargar el componente
        this.setDashboardFilters(this.filter);
    },
    beforeDestroy() {
        this.debouncedLocationSearch.cancel();
    },
    methods: {
        ...mapActions("tareas", ["obtenerEmpresas"]),
        ...mapActions("dashboard", [
            "findDataDash1",
            "obtenerIdEmpresaUsuario",
            "findDataDash1Priority",
            "findDataDash2",
            "findDataDash3",
        ]),
        ...mapMutations("dashboard", [
            "load",
            "resetDataDash1Consulted",
            "resetDataDash2Consulted",
            "resetDataDash3Consulted",
            "setDashboardFilters",
            "resetDashboardFilters"
        ]),
        ...mapActions("users", ["getUsers"]),
        ...mapMutations("users", ["setUsers", "resetUsers"]),

        async clearFilter() {
            this.filter = {
                company_id: this.viewFilterCompany
                    ? null
                    : await this.obtenerIdEmpresaUsuario(),
                slq_location_id: [],
                country_id: null,
                date_report: null,
                in_use: null,
                user_id: null,
            };
            this.resetDashboardFilters();
            this.resetDataDash1Consulted();
            this.resetDataDash2Consulted();
            this.resetDataDash3Consulted();
            this.resetUsers();

            await this.searchInfo();
            // Actualiza los filtros en Vuex después de limpiar
            this.setDashboardFilters(this.filter);
        },

        async searchInfo() {
            this.$emit("handle-loading", true);
            if (this.filter.company_id) {
                if (this.screens.accident && !this.dataDash1.consulted) {
                    await this.findDataDash1(this.filter);
                    const payloadAri = {
                        company_id: this.filter.company_id,
                        products_ids: (this.filter.slq_location_id.length > 0 || this.filter.date_report || this.filter.in_use)
                            ? this.dataDash1.product_company_ids
                            : [],
                    };
                    await this.findDataDash1Priority(payloadAri);
                }
                if (this.screens.health && !this.dataDash2.consulted) {
                    await this.findDataDash2(this.filter);
                }
                if (this.screens.usability && !this.dataDash3.consulted) {
                    await this.getUsers(this.filter.company_id);
                    await this.findDataDash3(this.filter);
                }
            }
            this.$emit("handle-loading", false);
        },

        /**
         * Búsqueda disparada por el filtro de ubicaciones. Va con debounce para que
         * marcar varias ubicaciones seguidas lance una sola consulta.
         */
        async searchInfoFromLocations() {
            this.resetDataDash1Consulted();
            this.resetDataDash2Consulted();
            await this.searchInfo();
            this.setDashboardFilters(this.filter);
        },

        async searchCompany(val) {
            await this.obtenerEmpresas(val);
        },
        normalizer(node) {
            return {
                id: node.id,
                label: node.label,
                children: Array.isArray(node.children) && node.children.length ? node.children : undefined,
                raw: node,
            };
        },
        /**
         * País efectivo de una ubicación: el propio si lo tiene, si no el heredado
         * del ancestro más cercano que lo tenga. Puede ser null (ubicaciones sin país).
         */
        resolveNodeCountryId(node, inheritedCountryId = null) {
            const own = node && node.company_country && node.company_country.country
                ? node.company_country.country.id
                : null;
            return own !== null ? own : inheritedCountryId;
        },
        /**
         * Recorre el árbol acumulando el país efectivo de cada ubicación por id.
         */
        collectCountryByLocationId(nodes, inheritedCountryId = null, map = {}) {
            if (!Array.isArray(nodes)) return map;

            for (const node of nodes) {
                const countryId = this.resolveNodeCountryId(node, inheritedCountryId);
                map[node.id] = countryId;

                if (Array.isArray(node.children) && node.children.length) {
                    this.collectCountryByLocationId(node.children, countryId, map);
                }
            }
            return map;
        },
        /**
         * ¿Esta ubicación pertenece a un país distinto al que quedó bloqueado por la
         * selección actual?.
         */
        isLockedByOtherCountry(locationId) {
            const lockedCountryId = this.lockedLocationCountryId;
            if (lockedCountryId === null) return false;

            const countryId = this.countryByLocationId[locationId];
            if (countryId === null || countryId === undefined) return false;

            return countryId !== lockedCountryId;
        },
        // Convierte una cadena a formato Title Case considerando reglas de idioma español
        toTitleCase(value) {
            if (!value || typeof value !== "string") return "";
            const normalized = value
                .toLocaleLowerCase("es-CO")
                .replace(/\s+/g, " ")
                .trim();
            return normalized.replace(/\b([\p{L}ÁÉÍÓÚÑáéíóúñ]+)\b/gu, (w) =>
                w.charAt(0).toLocaleUpperCase("es-CO") + w.slice(1)
            );
        },
    },
    computed: {
        ...mapState("tareas", ["empresas"]),
        ...mapState("dashboard", ["screens", "dataDash1", "dataDash2", "dataDash3", "dashboardFilters"]),
        ...mapState("users", ["users"]),
        ...mapState('configurableVariables', ['allCountries']),
        ...mapState('location', ['treeWithChildrens']),
        ...mapState('autenticacion', ['locations_user', 'my_company']),

        selectedCountryCode() {
            if (!this.filter.country_id) return null;
            const found = this.allCountries.find(c => c.country.id === this.filter.country_id);
            if (found) {
                return found.country.code.toUpperCase();
            }
            return null;
        },

        /**
         * País efectivo de cada ubicación del árbol, indexado por id. Sólo se recalcula
         * cuando cambia el árbol, no en cada selección.
         */
        countryByLocationId() {
            return this.collectCountryByLocationId(this.treeWithChildrens);
        },

        /**
         * País que queda "bloqueado" por la selección actual de ubicaciones: el de la
         * primera ubicación marcada que tenga país. Null si no hay nada marcado, lo que
         * vuelve a habilitar todo el árbol.
         */
        lockedLocationCountryId() {
            const selected = this.filter.slq_location_id;
            if (!Array.isArray(selected) || selected.length === 0) return null;

            for (const locationId of selected) {
                const countryId = this.countryByLocationId[locationId];
                if (countryId !== null && countryId !== undefined) return countryId;
            }
            return null;
        },
    },
    watch: {
        "filter.company_id": async function (val) {
            if (!val) {
                await this.clearFilter();
                this.$emit("company-id-changed", null);
                return;
            } else {
                this.filter.slq_location_id = [];
                await this.getAllCountriesByCompany(val);
                
                const mainCountry = this.allCountries && this.allCountries.find(c => c.is_main === 1);
                this.filter.country_id = mainCountry ? mainCountry.country.id : null;

                // Cargar ubicaciones filtradas por el país principal y con auto-selección
                const countryIdForTree = this.filter.country_id || null;
                if (this.locations_user && this.locations_user.length > 0) {
                    const selectedLocationIds = await this.loadLocationsTreeWithMultipleAutoSelect(val, this.locations_user, countryIdForTree, this.my_company);
                    if (selectedLocationIds && selectedLocationIds.length > 0) {
                        this.filter.slq_location_id = selectedLocationIds;
                    }
                } else {
                    await this.loadLocationsTree(val, countryIdForTree);
                }

                this.resetDataDash1Consulted();
                this.resetDataDash2Consulted();

                this.resetDataDash3Consulted();
                this.resetUsers();

                await this.searchInfo();
                this.$emit("company-id-changed", val);
            }
            // Actualiza los filtros en Vuex cada vez que company_id cambie
            this.setDashboardFilters(this.filter);
        },

        "filter.slq_location_id": {
            handler() {
                this.debouncedLocationSearch();
            },
            deep: true,
        },

        "filter.date_report": async function (val) {
            if (!val) {
                this.filter.date_report = null;
            }
            this.resetDataDash3Consulted();
            await this.searchInfo();
            // Actualiza los filtros en Vuex cada vez que date_report cambie
            this.setDashboardFilters(this.filter);
        },

        "filter.in_use": async function (val) {
            if (val === null) {
                this.filter.in_use = null;
            }
            this.resetDataDash1Consulted();
            this.resetDataDash2Consulted();
            await this.searchInfo();
            // Actualiza los filtros en Vuex cada vez que in_use cambie
            this.setDashboardFilters(this.filter);
        },

        "filter.user_id": async function (val) {
            if (val === null) {
                this.filter.user_id = null;
            }
            this.resetDataDash3Consulted();
            await this.searchInfo();
            // Actualiza los filtros en Vuex cada vez que user_id cambie
            this.setDashboardFilters(this.filter);
        },

        "filter.country_id": async function (newVal, oldVal) {
            if (this.filter.company_id && newVal !== oldVal) {
                const prevLocation = JSON.stringify(this.filter.slq_location_id);
                this.filter.slq_location_id = [];

                // Recargar el árbol filtrado por el nuevo país seleccionado
                const countryIdForTree = newVal || null;
                if (this.locations_user && this.locations_user.length > 0) {
                    const selectedLocationIds = await this.loadLocationsTreeWithMultipleAutoSelect(this.filter.company_id, this.locations_user, countryIdForTree, this.my_company);
                    if (selectedLocationIds && selectedLocationIds.length > 0) {
                        this.filter.slq_location_id = selectedLocationIds;
                    }
                } else {
                    await this.loadLocationsTree(this.filter.company_id, countryIdForTree);
                }

                if (prevLocation === JSON.stringify(this.filter.slq_location_id)) {
                    this.resetDataDash1Consulted();
                    this.resetDataDash2Consulted();
                    await this.searchInfo();
                    this.setDashboardFilters(this.filter);
                }
            }
        },

        screens: {
            handler: async function () {
                await this.searchInfo();
            },
            deep: true,
        },
    },
};
</script>

<style src="./../../../../node_modules/@riophae/vue-treeselect/dist/vue-treeselect.min.css"></style>
<style scoped>
.vue-treeselect__no-children-tip {
    display: none !important;
}

::v-deep .vue-treeselect__option:has(.ubicacion-otro-pais) .vue-treeselect__label-container {
    opacity: 0.45;
    pointer-events: none;
}

.vue-treeselect__control {
    height: 32px !important;
    min-height: 32px !important;
    font-size: 13px !important;
    border-radius: 4px !important;
}

.vue-treeselect__single-value {
    line-height: 36px !important;
}

.vue-treeselect__placeholder {
    line-height: 32px !important;
}

.imagen-checkbox {
    box-shadow: 0 0 3px #b8b8b8;
}

.is-checked .imagen-checkbox {
    box-shadow: 0px 1px 10px #097b83;
    border: 1px solid #097b83;
}

.checkbox-imagen .el-radio__input {
    display: none;
}

.flag-img-select {
  height: 15px;
}

.flag-img-option {
  height: 20px;
}

.flag-img-tree {
  height: 15px;
}

.flag-prefix-container {
  height: 100%;
  display: flex;
  align-items: center;
  padding-left: 5px;
}
</style>
